import { cn } from '@commenda/utils';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { type ChartColor, ChartColorHelper } from './chartColors';
import {
  AXIS_TICK,
  BOUNDARY_LABEL_PROPS,
  BOUNDARY_LINE_CLASS,
  ChartHelper,
  DEFAULT_VALUE_FORMATTER,
  GRID_CLASS,
  PRELIMINARY_BAR_OPACITY,
  PRELIMINARY_DASH,
} from './chartHelpers';
import type {
  ChartDatum,
  ChartTooltipRenderer,
  ChartValueFormatter,
  IChartTooltipEntry,
} from './chartTypes';

interface IComboSeries {
  categories: string[];
  colors?: ChartColor[];
  valueFormatter?: ChartValueFormatter;
  showYAxis?: boolean;
  yAxisWidth?: number;
  autoMinValue?: boolean;
  minValue?: number;
  maxValue?: number;
}

export interface IComboBarSeries extends IComboSeries {
  type?: 'default' | 'stacked';
}

export interface IComboLineSeries extends IComboSeries {
  connectNulls?: boolean;
}

export interface IComboChartProps {
  data: ChartDatum[];
  index: string;
  barSeries: IComboBarSeries;
  lineSeries: IComboLineSeries;
  /** Plots the line series against a second y-axis on the right. */
  enableBiaxial?: boolean;
  showLegend?: boolean;
  showGridLines?: boolean;
  showXAxis?: boolean;
  /** Default for both series' y-axes; a series' own `showYAxis` wins. */
  showYAxis?: boolean;
  /** Label only the first, last and up to four evenly spaced x values. */
  sparseXLabels?: boolean;
  customTooltip?: ChartTooltipRenderer;
  className?: string;
}

const LEFT_AXIS = 'left';
const RIGHT_AXIS = 'right';

export const ComboChart = ({
  data,
  index,
  barSeries,
  lineSeries,
  enableBiaxial = false,
  showLegend = true,
  showGridLines = true,
  showXAxis = true,
  showYAxis = true,
  sparseXLabels = false,
  customTooltip,
  className,
}: IComboChartProps) => {
  const barColors = ChartColorHelper.categoryColors(
    barSeries.categories,
    barSeries.colors
  );
  const lineColors = ChartColorHelper.categoryColors(
    lineSeries.categories,
    lineSeries.colors
  );
  const allColors = new Map([...barColors, ...lineColors]);
  const barFormatter = barSeries.valueFormatter ?? DEFAULT_VALUE_FORMATTER;
  const lineFormatter = lineSeries.valueFormatter ?? barFormatter;
  const lineAxisId = enableBiaxial ? RIGHT_AXIS : LEFT_AXIS;
  const lineCategories = new Set(lineSeries.categories);
  const Tip = customTooltip ?? ChartTooltip;
  const split = ChartHelper.splitPreliminary(
    data,
    index,
    lineSeries.categories
  );

  const withFormatting = (entry: IChartTooltipEntry): IChartTooltipEntry => ({
    ...entry,
    formattedValue: lineCategories.has(entry.category)
      ? lineFormatter(entry.value)
      : barFormatter(entry.value),
  });

  return (
    <div className={cn('flex h-80 w-full flex-col', className)}>
      {showLegend ? (
        <ChartLegend
          className="mb-sm"
          items={[
            ...barSeries.categories.map((c) => ({
              name: c,
              color: barColors.get(c) ?? 'gray',
              shape: 'bar' as const,
            })),
            ...lineSeries.categories.map((c) => ({
              name: c,
              color: lineColors.get(c) ?? 'gray',
              shape: 'line' as const,
            })),
          ]}
        />
      ) : null}
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={split.data}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            stackOffset={barSeries.type === 'stacked' ? 'sign' : undefined}
          >
            {showGridLines ? (
              <CartesianGrid
                className={GRID_CLASS}
                horizontal
                vertical={false}
              />
            ) : null}
            <XAxis
              dataKey={index}
              hide={!showXAxis}
              tickLine={false}
              axisLine={false}
              interval={sparseXLabels ? 0 : 'equidistantPreserveStart'}
              ticks={
                sparseXLabels ? ChartHelper.sparseTicks(data, index) : undefined
              }
              minTickGap={8}
              tick={AXIS_TICK}
              padding={{ left: 8, right: 8 }}
            />
            <YAxis
              yAxisId={LEFT_AXIS}
              width={barSeries.yAxisWidth ?? 56}
              hide={(barSeries.showYAxis ?? showYAxis) === false}
              tickLine={false}
              axisLine={false}
              domain={ChartHelper.yAxisDomain(
                barSeries.autoMinValue ?? false,
                barSeries.minValue,
                barSeries.maxValue
              )}
              tick={AXIS_TICK}
              tickFormatter={barFormatter}
              allowDecimals
            />
            {enableBiaxial ? (
              <YAxis
                yAxisId={RIGHT_AXIS}
                orientation="right"
                width={lineSeries.yAxisWidth ?? 56}
                hide={(lineSeries.showYAxis ?? showYAxis) === false}
                tickLine={false}
                axisLine={false}
                domain={ChartHelper.yAxisDomain(
                  lineSeries.autoMinValue ?? false,
                  lineSeries.minValue,
                  lineSeries.maxValue
                )}
                tick={AXIS_TICK}
                tickFormatter={lineFormatter}
                allowDecimals
              />
            ) : null}
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              isAnimationActive={false}
              cursor={{ className: 'fill-bg-secondary' }}
              content={({ active, payload, label }) => (
                <Tip
                  active={active}
                  label={ChartHelper.tooltipLabel(label)}
                  payload={ChartHelper.tooltipEntries(payload, allColors).map(
                    withFormatting
                  )}
                  valueFormatter={barFormatter}
                  preliminary={ChartHelper.tooltipPreliminary(payload)}
                />
              )}
            />
            {split.boundaryLabel !== null && (
              <ReferenceLine
                yAxisId={LEFT_AXIS}
                x={split.boundaryLabel}
                className={BOUNDARY_LINE_CLASS}
                stroke=""
                label={BOUNDARY_LABEL_PROPS}
              />
            )}
            {barSeries.categories.map((category) => (
              <Bar
                key={category}
                yAxisId={LEFT_AXIS}
                name={category}
                dataKey={category}
                stackId={barSeries.type === 'stacked' ? 'stack' : undefined}
                isAnimationActive={false}
                maxBarSize={40}
                className={ChartColorHelper.className(
                  barColors.get(category) ?? 'gray',
                  'fill'
                )}
                fill=""
              >
                {data.map((datum) => (
                  <Cell
                    key={String(datum[index])}
                    opacity={
                      ChartHelper.isPreliminary(datum)
                        ? PRELIMINARY_BAR_OPACITY
                        : 1
                    }
                  />
                ))}
              </Bar>
            ))}
            {lineSeries.categories.map((category) => (
              <Line
                key={category}
                yAxisId={lineAxisId}
                name={category}
                dataKey={category}
                type="linear"
                connectNulls={lineSeries.connectNulls ?? false}
                isAnimationActive={false}
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 4,
                  strokeWidth: 0,
                  className: ChartColorHelper.className(
                    lineColors.get(category) ?? 'gray',
                    'fill'
                  ),
                }}
                className={ChartColorHelper.className(
                  lineColors.get(category) ?? 'gray',
                  'stroke'
                )}
                stroke=""
              />
            ))}
            {[...split.preliminaryKeys].map(([category, key]) => (
              <Line
                key={key}
                yAxisId={lineAxisId}
                name={key}
                dataKey={key}
                type="linear"
                connectNulls={lineSeries.connectNulls ?? false}
                isAnimationActive={false}
                strokeWidth={2}
                strokeDasharray={PRELIMINARY_DASH}
                dot={false}
                activeDot={false}
                legendType="none"
                className={ChartColorHelper.className(
                  lineColors.get(category) ?? 'gray',
                  'stroke'
                )}
                stroke=""
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
